import axios from 'axios';
import jwtDecode from 'jwt-decode';

// Use direct API calls since CORS is now configured
const API_BASE_URL = 'https://api.bigclipboard.com';

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle token expiration
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      // Only redirect for auth-related endpoints, not for clipboard operations
      const url = error.config?.url || '';
      if (url.includes('/auth/')) {
        // Token expired or invalid
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth service
export const authService = {
  async register(userData) {
    const response = await api.post('/auth/register', userData);
    if (response.data.token) {
      localStorage.setItem('authToken', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
    }
    return response.data;
  },

  async login(credentials) {
    const response = await api.post('/auth/login', credentials);
    if (response.data.token) {
      localStorage.setItem('authToken', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
    }
    return response.data;
  },

  async refreshToken() {
    const response = await api.post('/auth/refresh');
    if (response.data.token) {
      localStorage.setItem('authToken', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
    }
    return response.data;
  },

  logout() {
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
  },

  getCurrentUser() {
    const user = localStorage.getItem('user');
    return user ? JSON.parse(user) : null;
  },

  isAuthenticated() {
    const token = localStorage.getItem('authToken');
    if (!token) return false;
    
    try {
      const decoded = jwtDecode(token);
      const currentTime = Date.now() / 1000;
      return decoded.exp > currentTime;
    } catch (error) {
      return false;
    }
  },

  getToken() {
    return localStorage.getItem('authToken');
  }
};

// Clipboard service
export const clipboardService = {
  async getItems(params = {}) {
    try {
      const response = await api.get('/clipboard', { params });
      return response.data;
    } catch (error) {
      console.error('=== API getItems ERROR ===');
      console.error('API call failed:', error);
      console.error('Error status:', error.response?.status);
      console.error('Error data:', error.response?.data);
      console.error('Error message:', error.message);
      console.error('=== API getItems ERROR END ===');
      throw error;
    }
  },

  async getItem(id) {
    try {
      const response = await api.get(`/clipboard/${id}`);
      return response.data;
    } catch (error) {
      console.error('=== API getItem ERROR ===');
      console.error('API call failed:', error);
      console.error('Error status:', error.response?.status);
      console.error('Error data:', error.response?.data);
      console.error('Error message:', error.message);
      console.error('=== API getItem ERROR END ===');
      throw error;
    }
  },

  async createItem(data) {
    const response = await api.post('/clipboard', data);
    return response.data;
  },

  async updateItem(id, data) {
    const response = await api.put(`/clipboard/${id}`, data);
    return response.data;
  },

  async deleteItem(id) {
    const response = await api.delete(`/clipboard/${id}`);
    return response.data;
  },

  async toggleFavorite(id) {
    try {
      const response = await api.post(`/clipboard/${id}/favorite`);
      return response.data;
    } catch (error) {
      console.error('=== API toggleFavorite ERROR ===');
      console.error('API call failed:', error);
      console.error('Error status:', error.response?.status);
      console.error('Error data:', error.response?.data);
      console.error('=== API toggleFavorite ERROR END ===');
      throw error;
    }
  },

  async togglePublic(id) {
    const response = await api.post(`/clipboard/${id}/public`);
    return response.data;
  },

  async getPublicItems(params = {}) {
    // Filter out empty parameters to avoid server errors
    const filteredParams = Object.fromEntries(
      Object.entries(params).filter(([key, value]) => 
        value !== null && value !== undefined && value !== ''
      )
    );
    const response = await api.get('/clipboard/public', { params: filteredParams });
    return response.data;
  },

  async getCategoryItems(categoryId, params = {}) {
    // Filter out empty parameters to avoid server errors
    const filteredParams = Object.fromEntries(
      Object.entries(params).filter(([key, value]) => 
        value !== null && value !== undefined && value !== ''
      )
    );
    // Add public=1 to get only public items
    filteredParams.public = 1;
    const response = await api.get(`/categories/${categoryId}/items`, { params: filteredParams });
    return response.data;
  },



  async getStats() {
    try {
      const response = await api.get('/clipboard/stats/summary');
      return response.data;
    } catch (error) {
      console.error('=== API getStats ERROR ===');
      console.error('API call failed:', error);
      console.error('Error status:', error.response?.status);
      console.error('Error data:', error.response?.data);
      console.error('Error message:', error.message);
      console.error('=== API getStats ERROR END ===');
      throw error;
    }
  },

  async getCategories() {
    try {
      const response = await api.get('/categories');
      return response.data;
    } catch (error) {
      console.error('=== API getCategories ERROR ===');
      console.error('API call failed:', error);
      console.error('Error status:', error.response?.status);
      console.error('Error data:', error.response?.data);
      console.error('Error message:', error.message);
      console.error('=== API getCategories ERROR END ===');
      throw error;
    }
  },



  async upvoteItem(id) {
    try {
      const response = await api.post(`/clipboard/${id}/upvote`);
      return response.data;
    } catch (error) {
      console.error('=== API upvoteItem ERROR ===');
      console.error('API call failed:', error);
      console.error('Error status:', error.response?.status);
      console.error('Error data:', error.response?.data);
      console.error('Error message:', error.message);
      console.error('=== API upvoteItem ERROR END ===');
      throw error;
    }
  },

  async downvoteItem(id) {
    try {
      const response = await api.post(`/clipboard/${id}/downvote`);
      return response.data;
    } catch (error) {
      console.error('=== API downvoteItem ERROR ===');
      console.error('API call failed:', error);
      console.error('Error status:', error.response?.status);
      console.error('Error data:', error.response?.data);
      console.error('Error message:', error.message);
      console.error('=== API downvoteItem ERROR END ===');
      throw error;
    }
  },

  async getUserVote(id) {
    try {
      const response = await api.get(`/clipboard/${id}/vote`);
      return response.data;
    } catch (error) {
      console.error('=== API getUserVote ERROR ===');
      console.error('API call failed:', error);
      console.error('Error status:', error.response?.status);
      console.error('Error data:', error.response?.data);
      console.error('Error message:', error.message);
      console.error('=== API getUserVote ERROR END ===');
      throw error;
    }
  },

  async deleteComment(itemId, commentId) {
    try {
      const response = await api.delete(`/clipboard/${itemId}/comments/${commentId}`);
      return response.data;
    } catch (error) {
      console.error('=== API deleteComment ERROR ===');
      console.error('API call failed:', error);
      console.error('Error status:', error.response?.status);
      console.error('Error data:', error.response?.data);
      console.error('Error message:', error.message);
      console.error('=== API deleteComment ERROR END ===');
      throw error;
    }
  },

  async createCategory(categoryData) {
    try {
      const response = await api.post('/categories', categoryData);
      return response.data;
    } catch (error) {
      console.error('=== API createCategory ERROR ===');
      console.error('API call failed:', error);
      console.error('Error status:', error.response?.status);
      console.error('Error data:', error.response?.data);
      console.error('Error message:', error.message);
      console.error('=== API createCategory ERROR END ===');
      throw error;
    }
  }
};

// Admin service
export const adminService = {
  async getUsers(params = {}) {
    try {
      const response = await api.get('/admin/users', { params });
      return response.data;
    } catch (error) {
      console.error('=== API getUsers ERROR ===');
      console.error('API call failed:', error);
      console.error('Error status:', error.response?.status);
      console.error('Error data:', error.response?.data);
      console.error('Error message:', error.message);
      console.error('=== API getUsers ERROR END ===');
      throw error;
    }
  },

  async updateUserRole(userId, isAdmin) {
    try {
      const response = await api.put(`/admin/users/${userId}/role`, { is_admin: isAdmin });
      return response.data;
    } catch (error) {
      console.error('=== API updateUserRole ERROR ===');
      console.error('API call failed:', error);
      console.error('Error status:', error.response?.status);
      console.error('Error data:', error.response?.data);
      console.error('Error message:', error.message);
      console.error('=== API updateUserRole ERROR END ===');
      throw error;
    }
  },

  async updateUserStatus(userId, status) {
    try {
      const response = await api.put(`/admin/users/${userId}/status`, { status });
      return response.data;
    } catch (error) {
      console.error('=== API updateUserStatus ERROR ===');
      console.error('API call failed:', error);
      console.error('Error status:', error.response?.status);
      console.error('Error data:', error.response?.data);
      console.error('Error message:', error.message);
      console.error('=== API updateUserStatus ERROR END ===');
      throw error;
    }
  },

  async updateUserGroup(userId, groupId) {
    try {
      const response = await api.put(`/admin/users/${userId}/group`, { group_id: groupId });
      return response.data;
    } catch (error) {
      console.error('=== API updateUserGroup ERROR ===');
      console.error('API call failed:', error);
      console.error('Error status:', error.response?.status);
      console.error('Error data:', error.response?.data);
      console.error('Error message:', error.message);
      console.error('=== API updateUserGroup ERROR END ===');
      throw error;
    }
  },

  async getUserDetails(userId) {
    try {
      const response = await api.get(`/admin/users/${userId}`);
      return response.data;
    } catch (error) {
      console.error('=== API getUserDetails ERROR ===');
      console.error('API call failed:', error);
      console.error('Error status:', error.response?.status);
      console.error('Error data:', error.response?.data);
      console.error('Error message:', error.message);
      console.error('=== API getUserDetails ERROR END ===');
      throw error;
    }
  },

  async getSystemStats() {
    try {
      const response = await api.get('/admin/stats');
      return response.data;
    } catch (error) {
      console.error('=== API getSystemStats ERROR ===');
      console.error('API call failed:', error);
      console.error('Error status:', error.response?.status);
      console.error('Error data:', error.response?.data);
      console.error('Error message:', error.message);
      console.error('=== API getSystemStats ERROR END ===');
      throw error;
    }
  },

  async getGroups() {
    try {
      const response = await api.get('/user-groups');
      return response.data;
    } catch (error) {
      console.error('=== API getGroups ERROR ===');
      console.error('API call failed:', error);
      console.error('Error status:', error.response?.status);
      console.error('Error data:', error.response?.data);
      console.error('Error message:', error.message);
      console.error('=== API getGroups ERROR END ===');
      throw error;
    }
  },

  async getCategories() {
    try {
      const response = await api.get('/categories');
      return response.data;
    } catch (error) {
      console.error('=== API getCategories ERROR ===');
      console.error('API call failed:', error);
      console.error('Error status:', error.response?.status);
      console.error('Error data:', error.response?.data);
      console.error('Error message:', error.message);
      console.error('=== API getCategories ERROR END ===');
      throw error;
    }
  }
};

// Health check service
export const healthService = {
  async checkHealth() {
    const response = await api.get('/health');
    return response.data;
  }
};

export default api; 